import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Paperclip, Loader2, Check, ArrowUpRight } from "lucide-react";
import { useT } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const AREAS = ["Produzione e stabilimento", "Montaggio e cantieri", "Ufficio tecnico e progettazione", "Amministrazione e commerciale", "Altro"];
const field = "mt-2 w-full border-b border-[#121110]/30 bg-transparent py-3 text-base outline-none transition-colors focus:border-[#C26D38]";
const lab = "font-mono text-xs uppercase tracking-[0.2em] text-[#6E685E]";

const CvUpload = ({ cv, setCv, t }) => {
  const pick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) return toast.error(t("job.errPdf", "Carica il CV in formato PDF"));
    if (file.size > 10 * 1024 * 1024) return toast.error(t("att.errSize", "File oltre 10 MB: ") + file.name);
    setCv({ name: file.name, id: null });
    const body = new FormData();
    body.append("file", file);
    try {
      const res = await axios.post(`${API}/quotes/attachments`, body);
      setCv({ name: file.name, id: res.data?.id ?? null });
    } catch {
      setCv(null);
      toast.error(t("att.errUpload", "Caricamento non riuscito: ") + file.name);
    }
  };
  return (
    <label className="flex cursor-pointer items-center gap-3 border border-dashed border-[#121110]/30 px-4 py-4 text-sm transition-colors hover:border-[#C26D38] sm:col-span-2" data-testid="career-cv">
      {cv ? (cv.id ? <Check className="h-4 w-4 text-[#C26D38]" /> : <Loader2 className="h-4 w-4 animate-spin" />) : <Paperclip className="h-4 w-4" />}
      <span className="min-w-0 flex-1 truncate">{cv ? cv.name : t("job.cv", "Allega il tuo CV (PDF, max 10 MB, facoltativo)")}</span>
      <input type="file" accept=".pdf" className="hidden" onChange={pick} data-testid="career-cv-input" />
    </label>
  );
};

export const CareerForm = ({ job = "", setJob = () => {} }) => {
  const t = useT();
  const [f, setF] = useState({ name: "", email: "", phone: "", city: "", area: AREAS[0], message: "", privacy: false });
  const [cv, setCv] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (cv && !cv.id) return toast.error(t("job.wait", "Attendi la fine del caricamento del CV"));
    setBusy(true);
    try {
      await axios.post(`${API}/careers`, { ...f, job, attachment_id: cv?.id ?? null });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail && typeof err.response.data.detail === "string" ? err.response.data.detail : t("job.err", "Invio non riuscito, riprova"));
    } finally {
      setBusy(false);
    }
  };

  if (done) return <p className="border border-[#121110]/15 bg-[#EFECE6] p-8 font-display text-2xl font-bold" data-testid="career-success">{t("job.ok", "Grazie! Abbiamo ricevuto la tua candidatura e ti ricontatteremo.")}</p>;
  return (
    <form onSubmit={submit} className="grid gap-8 sm:grid-cols-2" data-testid="career-form">
      {job && (
        <p className="flex flex-wrap items-center gap-3 border border-[#C26D38]/40 bg-[#C26D38]/10 px-4 py-3 text-sm sm:col-span-2" data-testid="career-job">
          <span>{t("jobs.for", "Candidatura per")}: <b>{job}</b></span>
          <button type="button" onClick={() => setJob("")} className="underline underline-offset-4" data-testid="career-job-clear">{t("jobs.spontaneous", "Rendila spontanea")}</button>
        </p>
      )}
      <label className="block"><span className={lab}>{t("job.name", "Nome e cognome")} *</span><input required minLength={2} value={f.name} onChange={set("name")} className={field} data-testid="career-name" /></label>
      <label className="block"><span className={lab}>Email *</span><input required type="email" value={f.email} onChange={set("email")} className={field} data-testid="career-email" /></label>
      <label className="block"><span className={lab}>{t("job.phone", "Telefono")}</span><input value={f.phone} onChange={set("phone")} className={field} data-testid="career-phone" /></label>
      <label className="block"><span className={lab}>{t("job.city", "Città")}</span><input value={f.city} onChange={set("city")} className={field} data-testid="career-city" /></label>
      <label className="block sm:col-span-2"><span className={lab}>{t("job.area", "Area di interesse")} *</span>
        <select value={f.area} onChange={set("area")} className={field} data-testid="career-area">
          {AREAS.map((a) => <option key={a} value={a}>{t(`job.area.${AREAS.indexOf(a)}`, a)}</option>)}
        </select>
      </label>
      <label className="block sm:col-span-2"><span className={lab}>{t("job.msg", "Presentati in poche righe")} *</span><textarea required minLength={10} rows={5} value={f.message} onChange={set("message")} className={field} data-testid="career-message" /></label>
      <CvUpload cv={cv} setCv={setCv} t={t} />
      <label className="flex items-start gap-3 text-sm sm:col-span-2">
        <input required type="checkbox" checked={f.privacy} onChange={set("privacy")} className="mt-1 h-4 w-4 accent-[#C26D38]" data-testid="career-privacy" />
        <span>{t("job.privacy", "Ho letto la Privacy policy e acconsento al trattamento dei miei dati per la selezione del personale.")} <a href="/privacy-policy" className="underline underline-offset-4">Privacy policy</a></span>
      </label>
      <button type="submit" disabled={busy} className="btn-amber justify-self-start disabled:opacity-60" data-testid="career-submit">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {t("job.send", "Invia candidatura")} <ArrowUpRight className="h-4 w-4" />
      </button>
    </form>
  );
};
